/**
 * CategorySettingsPage — F12.B T12.B.5.
 *
 * Zarządzanie typami rekordów modułu.
 * Routing: `/modules/:id/categories` (tenant scope).
 *
 * Funkcje:
 *  - Lista typów (karta per typ: ikona, kolor, badge default, badge nieaktywny)
 *  - Tworzenie nowego typu (modal)
 *  - Edycja istniejącego (modal)
 *  - Soft-delete (confirm)
 *  - Zmiana kolejności (sort_order)
 *  - Max 50 typów per moduł (info banner gdy limit osiągnięty)
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ArrowLeft, Edit2, FileText, MoreVertical, Plus, Star, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useTabLabel } from '@/components/tabs/useTabLabel';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface Category {
  id: string;
  moduleId: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string;
  color: string;
  isActive: boolean;
  isDefault: boolean;
  showAsTab: boolean;
  sortOrder: number;
}

interface ModuleInfo {
  id: string;
  name: string;
  slug: string;
}

const COLOR_PRESETS = [
  '#6B7280',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#F97316',
  '#6366F1',
];

const SLUG_REGEX = /^[a-z][a-z0-9_]{0,99}$/;

const MAX_RECORD_TYPES = 50;

interface TypeFormState {
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  isDefault: boolean;
  showAsTab: boolean;
  sortOrder: number;
}

const DEFAULT_FORM: TypeFormState = {
  name: '',
  slug: '',
  description: '',
  icon: 'FileText',
  color: '#6B7280',
  isDefault: false,
  showAsTab: true,
  sortOrder: 0,
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/^([^a-z])/, 'r$1')
    .slice(0, 100);
}

interface CategorySettingsPageProps {
  /** Gdy renderowane wewnątrz ModuleSettingsPage — ukrywa nagłówek strony i back button. */
  embedded?: boolean;
  /** Override moduleId z propsa (gdy embedded). Domyślnie z URL params. */
  moduleId?: string;
}

export function CategorySettingsPage({
  embedded,
  moduleId: moduleIdProp,
}: CategorySettingsPageProps = {}) {
  const params = useParams<{ id: string }>();
  const moduleId = moduleIdProp ?? params.id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<Category | null>(null);
  const [form, setForm] = useState<TypeFormState>(DEFAULT_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof TypeFormState, string>>>({});
  const [slugAutoSync, setSlugAutoSync] = useState(true);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Category | null>(null);

  // Pobranie modułu (dla breadcrumba)
  const moduleQuery = useQuery({
    queryKey: ['module', moduleId],
    queryFn: async () => {
      const res = await api.get<{ module: ModuleInfo }>(`/modules/${moduleId}`);
      return res.data.module;
    },
    enabled: !!moduleId,
  });
  useTabLabel(
    !embedded && moduleQuery.data
      ? t('tenant.modules.categories.title', { module: moduleQuery.data.name })
      : null,
  );

  // Pobranie typów rekordów
  const typesQuery = useQuery({
    queryKey: ['module-categories', moduleId],
    queryFn: async () => {
      const res = await api.get<{ categories: Category[] }>(`/modules/${moduleId}/categories`);
      return res.data.categories;
    },
    enabled: !!moduleId,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await api.post(`/modules/${moduleId}/categories`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.categories.toasts.created') });
      queryClient.invalidateQueries({ queryKey: ['module-categories', moduleId] });
      closeModal();
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await api.put(`/modules/${moduleId}/categories/${editingType!.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.categories.toasts.updated') });
      queryClient.invalidateQueries({ queryKey: ['module-categories', moduleId] });
      closeModal();
    },
    onError: handleMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: async (typeId: string) => {
      await api.delete(`/modules/${moduleId}/categories/${typeId}`);
      return typeId;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.categories.toasts.deleted') });
      queryClient.invalidateQueries({ queryKey: ['module-categories', moduleId] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.categories.toasts.deleteError') });
    },
  });

  function handleMutationError(err: unknown) {
    if (isAxiosError(err)) {
      const body = err.response?.data as { error?: string } | undefined;
      if (body?.error === 'Slug already exists in module') {
        setFormErrors((p) => ({ ...p, slug: t('tenant.modules.categories.errors.slugExists') }));
        return;
      }
      if (typeof body?.error === 'string' && body.error.includes('Maksimum')) {
        toast.show({ variant: 'error', title: body.error });
        return;
      }
    }
    toast.show({
      variant: 'error',
      title: editingType
        ? t('tenant.modules.categories.toasts.updateError')
        : t('tenant.modules.categories.toasts.createError'),
    });
  }

  function openCreateModal() {
    const types = typesQuery.data ?? [];
    setEditingType(null);
    setForm({ ...DEFAULT_FORM, sortOrder: types.length });
    setFormErrors({});
    setSlugAutoSync(true);
    setShowModal(true);
  }

  function openEditModal(rt: Category) {
    setEditingType(rt);
    setForm({
      name: rt.name,
      slug: rt.slug,
      description: rt.description ?? '',
      icon: rt.icon,
      color: rt.color,
      isDefault: rt.isDefault,
      showAsTab: rt.showAsTab,
      sortOrder: rt.sortOrder,
    });
    setFormErrors({});
    setSlugAutoSync(false);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingType(null);
    setForm(DEFAULT_FORM);
    setFormErrors({});
    setSlugAutoSync(true);
  }

  function handleNameChange(name: string) {
    const next: Partial<TypeFormState> = { name };
    if (slugAutoSync && !editingType) {
      next.slug = slugify(name);
    }
    setForm((p) => ({ ...p, ...next }));
    if (formErrors.name) setFormErrors((p) => ({ ...p, name: undefined }));
  }

  function handleSlugChange(slug: string) {
    setSlugAutoSync(false);
    setForm((p) => ({ ...p, slug }));
    if (formErrors.slug) setFormErrors((p) => ({ ...p, slug: undefined }));
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof TypeFormState, string>> = {};
    if (!form.name.trim()) errs.name = t('tenant.modules.categories.errors.nameRequired');
    if (!form.slug) errs.slug = t('tenant.modules.categories.errors.slugRequired');
    else if (!SLUG_REGEX.test(form.slug))
      errs.slug = t('tenant.modules.categories.errors.slugInvalid');
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    const payload = {
      name: form.name.trim(),
      slug: form.slug,
      description: form.description.trim() || null,
      icon: form.icon,
      color: form.color,
      isDefault: form.isDefault,
      showAsTab: form.showAsTab,
      sortOrder: form.sortOrder,
    };
    if (editingType) updateMutation.mutate(payload);
    else createMutation.mutate(payload);
  }

  function handleDelete(rt: Category) {
    setOpenMenu(null);
    setPendingDelete(rt);
  }

  // Zamknij dropdown menu przy kliknięciu poza
  useEffect(() => {
    if (!openMenu) return;
    function handler() {
      setOpenMenu(null);
    }
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [openMenu]);

  // ─── Guards ───────────────────────────────────────────────────────────────

  if (!moduleId) return <main className="p-6">Brak ID modułu</main>;

  if (moduleQuery.isLoading || typesQuery.isLoading) {
    const spinner = (
      <div className="flex items-center justify-center p-6">
        <Spinner />
      </div>
    );
    return embedded ? (
      spinner
    ) : (
      <main className="flex min-h-full items-center justify-center bg-bg-secondary p-6">
        {spinner}
      </main>
    );
  }

  const module = moduleQuery.data;
  const types = typesQuery.data ?? [];
  const atLimit = types.length >= MAX_RECORD_TYPES;
  const loading = createMutation.isPending || updateMutation.isPending;

  const Wrapper = embedded ? 'div' : 'main';
  const wrapperClass = embedded ? 'space-y-4' : 'min-h-full bg-bg-secondary p-6';

  return (
    <Wrapper className={wrapperClass} data-testid="record-type-settings-page">
      <div className={embedded ? 'space-y-4' : 'space-y-4'}>
        {/* Header — pomijany gdy embedded (hub ma swój nagłówek) */}
        {!embedded && (
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate(`/modules/${moduleId}/builder`)}
                aria-label={t('common.back')}
                className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-bold text-text-primary">
                  {t('tenant.modules.categories.title', { module: module?.name ?? '' })}
                </h1>
                <p className="text-xs text-text-tertiary">
                  {t('tenant.modules.categories.subtitle')}
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={openCreateModal}
              disabled={atLimit}
              data-testid="add-record-type-btn"
            >
              {t('tenant.modules.categories.addNew')}
            </Button>
          </header>
        )}

        {/* Add button widoczny gdy embedded — bez nagłówka strony */}
        {embedded && (
          <div className="flex justify-end">
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={openCreateModal}
              disabled={atLimit}
              data-testid="add-record-type-btn"
            >
              {t('tenant.modules.categories.addNew')}
            </Button>
          </div>
        )}

        {atLimit && (
          <div className="rounded-md border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-700">
            {t('tenant.modules.categories.limitReached', { max: MAX_RECORD_TYPES })}
          </div>
        )}

        {/* Lista typów */}
        {types.length === 0 ? (
          <Card padding="lg">
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <FileText className="h-10 w-10 text-text-muted" />
              <p className="font-medium text-text-primary">
                {t('tenant.modules.categories.empty.title')}
              </p>
              <p className="text-sm text-text-tertiary">
                {t('tenant.modules.categories.empty.subtitle')}
              </p>
              <Button
                variant="primary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={openCreateModal}
                className="mt-2"
              >
                {t('tenant.modules.categories.addNew')}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-2">
            {types.map((rt) => (
              <Card key={rt.id} padding="md">
                <div className="flex items-center gap-3">
                  {/* Kolor + ikona */}
                  <div
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                    style={{ backgroundColor: rt.color + '20', color: rt.color }}
                  >
                    <FileText className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-text-primary">{rt.name}</span>
                      <code className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs text-text-muted">
                        {rt.slug}
                      </code>
                      {rt.isDefault && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                          <Star className="h-3 w-3" />
                          {t('tenant.modules.categories.badge.default')}
                        </span>
                      )}
                      {!rt.isActive && (
                        <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-xs text-text-muted">
                          {t('tenant.modules.categories.badge.inactive')}
                        </span>
                      )}
                      {!rt.showAsTab && (
                        <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-xs text-text-muted">
                          {t('tenant.modules.categories.badge.hiddenTab')}
                        </span>
                      )}
                    </div>
                    {rt.description && (
                      <p className="mt-0.5 truncate text-xs text-text-tertiary">{rt.description}</p>
                    )}
                  </div>

                  {/* Akcje */}
                  <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setOpenMenu(openMenu === rt.id ? null : rt.id)}
                      className="rounded-md p-1.5 text-text-muted hover:bg-bg-tertiary"
                      aria-label={t('common.actions')}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {openMenu === rt.id && (
                      <div className="absolute right-0 top-8 z-10 min-w-[160px] rounded-md border border-border-primary bg-bg-primary shadow-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setOpenMenu(null);
                            openEditModal(rt);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-bg-secondary"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                          {t('common.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(rt)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error-600 hover:bg-error-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('common.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal tworzenia / edycji */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={
          editingType
            ? t('tenant.modules.categories.modal.editTitle')
            : t('tenant.modules.categories.modal.createTitle')
        }
        size="md"
      >
        <div className="space-y-4">
          {/* Nazwa */}
          <FormField
            label={t('tenant.modules.categories.form.name')}
            error={formErrors.name}
            required
          >
            <Input
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={t('tenant.modules.categories.form.namePlaceholder')}
              data-testid="rt-name-input"
            />
          </FormField>

          {/* Slug */}
          <FormField
            label={t('tenant.modules.categories.form.slug')}
            error={formErrors.slug}
            hint={t('tenant.modules.categories.form.slugHint')}
            required
          >
            <Input
              value={form.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="np. serwisowy"
              disabled={!!editingType}
              data-testid="rt-slug-input"
            />
          </FormField>

          {/* Opis */}
          <FormField label={t('tenant.modules.categories.form.description')}>
            <Input
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder={t('tenant.modules.categories.form.descriptionPlaceholder')}
            />
          </FormField>

          {/* Kolor */}
          <FormField label={t('tenant.modules.categories.form.color')}>
            <div className="flex flex-wrap items-center gap-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, color: c }))}
                  className="h-6 w-6 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c,
                    borderColor: form.color === c ? 'white' : 'transparent',
                    outline: form.color === c ? `2px solid ${c}` : 'none',
                  }}
                  aria-label={c}
                />
              ))}
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                title={t('tenant.modules.categories.form.customColor')}
              />
            </div>
          </FormField>

          {/* Checkboxy */}
          <div className="flex flex-col gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => setForm((p) => ({ ...p, isDefault: e.target.checked }))}
                className="h-4 w-4 rounded border-border-primary text-primary-600"
              />
              {t('tenant.modules.categories.form.isDefault')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={form.showAsTab}
                onChange={(e) => setForm((p) => ({ ...p, showAsTab: e.target.checked }))}
                className="h-4 w-4 rounded border-border-primary text-primary-600"
              />
              {t('tenant.modules.categories.form.showAsTab')}
            </label>
          </div>

          {/* Kolory podgląd */}
          <div
            className="flex items-center gap-2 rounded-lg px-3 py-2"
            style={{ backgroundColor: form.color + '20' }}
          >
            <div className="h-5 w-5 rounded" style={{ backgroundColor: form.color }} />
            <span className="text-sm font-medium" style={{ color: form.color }}>
              {form.name || t('tenant.modules.categories.form.namePlaceholder')}
            </span>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={closeModal} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            data-testid="rt-submit-btn"
          >
            {editingType ? t('common.save') : t('tenant.modules.categories.modal.createBtn')}
          </Button>
        </div>
      </Modal>

      {/* Confirm delete dialog — zastępuje wbudowany window.confirm (reguła CLAUDE.md §4.12) */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.modules.categories.modal.deleteTitle', {
          defaultValue: 'Usunąć kategorię?',
        })}
        message={
          pendingDelete
            ? t('tenant.modules.categories.confirmDelete', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate(pendingDelete.id);
            setPendingDelete(null);
          }
        }}
        onClose={() => setPendingDelete(null)}
      />
    </Wrapper>
  );
}
